import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard, RoleGuard,StandardURL } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner"; // Import loader
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrashAlt, faPiggyBank, faInfoCircle } from "@fortawesome/free-solid-svg-icons"; // Icons for actions and fields

const BankDetailsPage = () => {
  const [bankDetails, setBankDetails] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newBankDetail, setNewBankDetail] = useState({
    bankaccount: "",
    bankcode: "",
    sendernames: "",
    threshhold: "",
    phonenumber: "",
    settlementtype: "bank", // Default to bank
  });
  const [updateBankDetail, setUpdateBankDetail] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const token = localStorage.getItem("token");
  const propertyId = localStorage.getItem("selectedPropertyId");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      if (token && propertyId) {
        try {
          // Fetch bank details
          const bankDetailsResponse = await axios.get(
            `${StandardURL}/iNyumba/admin/propertysettlementdetailsbank/viewbankdetails`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              params: {
                propertyid: propertyId, // Pass propertyid as a query parameter
              },
            }
          );
          // Ensure response is an array, even if API returns a single object
          setBankDetails(Array.isArray(bankDetailsResponse.data.response) ? bankDetailsResponse.data.response : [bankDetailsResponse.data.response]);
        } catch (error) {
          console.error("Error fetching bank details:", error);
          // If 404, it means no data found, which is not an error that needs "Try Again"
          if (axios.isAxiosError(error) && error.response && error.response.status === 404) {
            setBankDetails([]); // Set to empty array to show "Add Account" option
            toast.info("No settlement accounts found for this property. Please add one.");
          } else {
            setError(error.message || "Failed to fetch bank details.");
            toast.error(
              error.response?.data?.response ||
                "Failed to fetch bank details. Please try again."
            );
          }
        }

        try {
          // Fetch banks
          const banksResponse = await axios.get(
            `${StandardURL}/iNyumba/admin/bank/fetch`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setBanks(banksResponse.data.response);
        } catch (error) {
          console.error("Error fetching banks:", error);
          setError(error.message || "Failed to fetch banks.");
          toast.error(
            error.response?.data?.response ||
              "Failed to fetch banks. Please try again."
          );
        }
      } else {
          setError("Authentication token or property ID missing. Please ensure you are logged in and a property is selected.");
          toast.error("Authentication token or property ID missing.");
      }
      setLoading(false);
    };

    fetchData();
  }, [token, propertyId]);

  // Handle add bank detail
  const handleAddBankDetail = async () => {
    setLoading(true);
    if (token) {
      const payload = { ...newBankDetail, propertyid: propertyId };

      try {
        const response = await axios.post(
          `${StandardURL}/iNyumba/admin/propertysettlementdetailsbank/createsettlementdetailsstore`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setBankDetails((prevDetails) => [
          ...prevDetails,
          response.data.response,
        ]);
        setShowAddModal(false); // Close modal
        setNewBankDetail({ // Reset form
          bankaccount: "",
          bankcode: "",
          sendernames: "",
          threshhold: "",
          phonenumber: "",
          settlementtype: "bank",
        });
        toast.success("Bank account added successfully!");
      } catch (error) {
        console.error("Error adding bank detail:", error);
        toast.error(error.response?.data?.response || "Failed to add bank account.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteBankAccount = async (propertyIdToDelete) => {
    if (
      window.confirm("Are you sure you want to delete this settlement account?")
    ) {
      setLoading(true);
      try {
        const response = await axios.delete(
          `${StandardURL}/iNyumba/admin/propertysettlementdetailsbank/deletebankdetails`,
          {
            params: { propertyid: propertyIdToDelete }, // Pass as query parameter
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(
          response.data.response || "Settlement deleted successfully."
        );
        setBankDetails((prev) =>
          prev.filter((detail) => detail.propertyid !== propertyIdToDelete)
        );
      } catch (error) {
        console.error("Error deleting settlement account:", error);
        toast.error(
          error.response?.data?.response ||
            "Failed to delete settlement account. Please try again."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle update bank detail
  const handleUpdateBankDetail = async () => {
    setLoading(true);
    if (token && updateBankDetail) {
      const payload = { ...updateBankDetail, propertyid: propertyId };

      try {
        await axios.patch(
          `${StandardURL}/iNyumba/admin/propertysettlementdetailsbank/updatebankdetails`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setBankDetails((prevDetails) =>
          prevDetails.map((detail) =>
            detail.id === updateBankDetail.id
              ? { ...detail, ...updateBankDetail }
              : detail
          )
        );
        setShowUpdateModal(false); // Close modal
        toast.success("Bank account updated successfully!");
      } catch (error) {
        console.error("Error updating bank detail:", error);
        toast.error(error.response?.data?.response || "Failed to update bank account.");
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <Oval
            height={50}
            width={50}
            color="#4F46E5"
            wrapperStyle={{}}
            wrapperClass=""
            visible={true}
            ariaLabel='oval-loading'
            secondaryColor="#A5B4FC"
            strokeWidth={4}
            strokeWidthSecondary={4}
          />
          <p className="ml-3 text-lg text-gray-700">Loading settlement details...</p>
        </div>
      </AuthGuard>
    );
  }

  if (error) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center">
            <h2 className="text-2xl font-semibold text-red-600 mb-4">Error!</h2>
            <p className="text-gray-700">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
            >
              Try Again
            </button>
          </div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          
          <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-8 border-b pb-4">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-4 sm:mb-0">
                <FontAwesomeIcon icon={faPiggyBank} className="mr-3 text-green-600" />
                Settlement Details
              </h1>
              {bankDetails.length === 0 && ( // Only show "Add New Account" button in header if no accounts exist
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-all duration-300 flex items-center"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Add New Account
                </button>
              )}
            </div>

            {/* Bank Details List */}
            {bankDetails.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Account Name
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Bank Name
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Account Number
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Phone Number
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Settlement Type
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Threshold
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {bankDetails.map((detail) => (
                      <tr key={detail.id} className="hover:bg-gray-50">
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                          {detail.sendername}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                          {banks.find(bank => bank.bankcode === detail.bankcode)?.bankname || detail.bankcode}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                          {detail.bankaccount}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                          {detail.phonenumber}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${detail.settlementtype === 'bank' ? 'bg-indigo-100 text-indigo-800' : 'bg-green-100 text-green-800'}`}>
                            {detail.settlementtype?.toUpperCase() || 'N/A'} {/* FIX APPLIED HERE */}
                          </span>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                          KES {parseFloat(detail.threshhold).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <button
                            onClick={() => {
                              setUpdateBankDetail(detail);
                              setShowUpdateModal(true);
                            }}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-1.5 px-3 rounded-md text-sm transition-colors duration-200 mr-2"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteBankAccount(detail.propertyid)}
                            className="bg-red-500 hover:bg-red-600 text-white font-bold py-1.5 px-3 rounded-md text-sm transition-colors duration-200"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-gray-400 mb-4" />
                    <p className="text-lg text-gray-600">No settlement details found for this property.</p>
                    <p className="text-md text-gray-500 mt-2">Click "Add New Account" to get started.</p>
                </div>
            )}
          </div>

          {/* Add Account Modal */}
          {showAddModal && (
            <div className="fixed inset-0 z-50 bg-gray-800 bg-opacity-75 flex items-center justify-center p-4">
              <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg transform transition-all duration-300 scale-100 opacity-100">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Add New Bank Account</h2>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="add-account-number" className="block text-sm font-medium text-gray-700">Account Number</label>
                    <input
                      id="add-account-number"
                      type="number"
                      placeholder="e.g., 1234567890"
                      value={newBankDetail.bankaccount}
                      onChange={(e) =>
                        setNewBankDetail({
                          ...newBankDetail,
                          bankaccount: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="add-bank-select" className="block text-sm font-medium text-gray-700">Select Bank</label>
                    <select
                      id="add-bank-select"
                      value={newBankDetail.bankcode}
                      onChange={(e) =>
                        setNewBankDetail({
                          ...newBankDetail,
                          bankcode: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">-- Select Bank --</option>
                      {banks.map((bank) => (
                        <option key={bank.bankcode} value={bank.bankcode}>
                          {bank.bankname}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="add-account-name" className="block text-sm font-medium text-gray-700">Account Name</label>
                    <input
                      id="add-account-name"
                      type="text"
                      placeholder="e.g., John Doe"
                      value={newBankDetail.sendernames}
                      onChange={(e) =>
                        setNewBankDetail({
                          ...newBankDetail,
                          sendernames: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="add-threshold" className="block text-sm font-medium text-gray-700">Threshold (KES)</label>
                    <input
                      id="add-threshold"
                      type="number"
                      placeholder="e.g., 50000"
                      value={newBankDetail.threshhold}
                      onChange={(e) =>
                        setNewBankDetail({
                          ...newBankDetail,
                          threshhold: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="add-phone-number" className="block text-sm font-medium text-gray-700">Phone Number</label>
                    <input
                      id="add-phone-number"
                      type="text"
                      placeholder="e.g., 2547XXXXXXXX"
                      value={newBankDetail.phonenumber}
                      onChange={(e) =>
                        setNewBankDetail({
                          ...newBankDetail,
                          phonenumber: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="add-settlement-type" className="block text-sm font-medium text-gray-700">Settlement Type</label>
                    <select
                      id="add-settlement-type"
                      value={newBankDetail.settlementtype}
                      onChange={(e) =>
                        setNewBankDetail({
                          ...newBankDetail,
                          settlementtype: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="bank">Bank</option>
                      <option value="mpesa">M-Pesa</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddBankDetail}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
                  >
                    Save Account
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Update Account Modal */}
          {showUpdateModal && updateBankDetail && (
            <div className="fixed inset-0 z-50 bg-gray-800 bg-opacity-75 flex items-center justify-center p-4">
              <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg transform transition-all duration-300 scale-100 opacity-100">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Update Bank Account</h2>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="update-account-number" className="block text-sm font-medium text-gray-700">Account Number</label>
                    <input
                      id="update-account-number"
                      type="number"
                      placeholder="Account Number"
                      value={updateBankDetail.bankaccount}
                      onChange={(e) =>
                        setUpdateBankDetail({
                          ...updateBankDetail,
                          bankaccount: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="update-bank-select" className="block text-sm font-medium text-gray-700">Select Bank</label>
                    <select
                      id="update-bank-select"
                      value={updateBankDetail.bankcode}
                      onChange={(e) =>
                        setUpdateBankDetail({
                          ...updateBankDetail,
                          bankcode: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">-- Select Bank --</option>
                      {banks.map((bank) => (
                        <option key={bank.bankcode} value={bank.bankcode}>
                          {bank.bankname}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="update-account-name" className="block text-sm font-medium text-gray-700">Account Name</label>
                    <input
                      id="update-account-name"
                      type="text"
                      placeholder="Account Names"
                      value={updateBankDetail.sendernames}
                      onChange={(e) =>
                        setUpdateBankDetail({
                          ...updateBankDetail,
                          sendernames: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="update-threshold" className="block text-sm font-medium text-gray-700">Threshold (KES)</label>
                    <input
                      id="update-threshold"
                      type="number"
                      placeholder="Threshhold you want your money remitted at"
                      value={updateBankDetail.threshhold}
                      onChange={(e) =>
                        setUpdateBankDetail({
                          ...updateBankDetail,
                          threshhold: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="update-phone-number" className="block text-sm font-medium text-gray-700">Phone Number</label>
                    <input
                      id="update-phone-number"
                      type="text"
                      placeholder="Phone Number"
                      value={updateBankDetail.phonenumber}
                      onChange={(e) =>
                        setUpdateBankDetail({
                          ...updateBankDetail,
                          phonenumber: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="update-settlement-type" className="block text-sm font-medium text-gray-700">Settlement Type</label>
                    <select
                      id="update-settlement-type"
                      value={updateBankDetail.settlementtype}
                      onChange={(e) =>
                        setUpdateBankDetail({
                          ...updateBankDetail,
                          settlementtype: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="bank">Bank</option>
                      <option value="mpesa">M-Pesa</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowUpdateModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateBankDetail}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
                  >
                    Update Account
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default BankDetailsPage;